import React, { useMemo } from 'react';
import { Alert, Button, Card, Col, Row, Space, Table, Tag, Typography, message } from 'antd';
import { PrinterOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useOwnerGroupDashboard } from '@/hooks/useOwnerIntelligence';
import {
  OiPageFrame,
  PrimaryKpiCard,
  SecondaryKpiCard,
  WorryAboutTodayPanel,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { scoreToSignalTier } from '@/components/owner-intelligence/oi-signals';
import { openEngineReportPrint } from '@/components/print/openEngineReportPrint';
import { useAuthStore } from '@/store/authStore';
import { formatMoney } from '@/utils/format';
import type {
  GroupCompanyRow,
  GroupGmDashboardResult,
  GroupTopProject,
} from '@/services/ownerIntelligence.service';

const { Text } = Typography;

function moneyInCurrency(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null || !Number.isFinite(Number(amount))) return '—';
  return formatMoney(Number(amount), { currency: currency || undefined });
}

function currencyModeLabel(mode: GroupGmDashboardResult['group']['currency_mode']): string {
  if (mode === 'converted') return 'Converted to reporting currency';
  if (mode === 'mixed') return 'Partial FX (some companies excluded)';
  return 'Single currency';
}

const GroupGmDashboardPage: React.FC = () => {
  const { data, isLoading, error, refetch, isFetching } = useOwnerGroupDashboard();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const switchCompany = useAuthStore((s) => s.switchCompany);
  const [switchingId, setSwitchingId] = React.useState<string | null>(null);

  const totals = data?.group.totals;
  const reportingCurrency = data?.group.reporting_currency || data?.group.currency;
  const currencyMode = data?.group.currency_mode ?? 'single';
  const hasReportingTotals = Boolean(totals) && currencyMode !== 'mixed';
  const showPartialTotals = Boolean(totals) && currencyMode === 'mixed';

  const drillToCompany = async (row: GroupCompanyRow, path: string) => {
    if (row.company_id === user?.company_id) {
      navigate(path);
      return;
    }
    setSwitchingId(row.company_id);
    try {
      await switchCompany(row.company_id);
      await queryClient.clear();
      navigate(path, { replace: true });
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Could not switch company'
      );
    } finally {
      setSwitchingId(null);
    }
  };

  const handlePrint = (payload: GroupGmDashboardResult) => {
    const g = payload.group;
    const cur = g.reporting_currency || g.currency;
    const t = g.totals;
    openEngineReportPrint({
      docType: 'oi_group_gm_dashboard',
      title: 'GROUP GM DASHBOARD',
      documentNumber: `GROUP-GM-${new Date().toISOString().slice(0, 10)}`,
      partyName: `${g.company_count} compan${g.company_count === 1 ? 'y' : 'ies'}`,
      metaLines: [
        { label: 'As of', value: new Date(payload.as_of).toLocaleString() },
        { label: 'Reporting currency', value: cur || '—' },
        { label: 'Currency mode', value: currencyModeLabel(g.currency_mode) },
        { label: 'Companies', value: String(g.company_count) },
        {
          label: 'FX converted',
          value: String(g.fx_converted_company_count ?? 0),
        },
      ],
      columns: [
        { key: 'description', label: 'Company' },
        { key: 'ccy', label: 'CCY' },
        { key: 'sales_mtd', label: 'Sales MTD', align: 'right' },
        { key: 'gp', label: 'GP YTD', align: 'right' },
        { key: 'np', label: 'Net profit', align: 'right' },
        { key: 'cash', label: 'Cash', align: 'right' },
        { key: 'ar', label: 'AR', align: 'right' },
        { key: 'inv', label: 'Inventory', align: 'right' },
        { key: 'neg_mgn', label: 'Neg margin', align: 'right' },
      ],
      lines: payload.companies.map((c, i) => {
        const k = c.kpis_reporting ?? c.kpis;
        const displayCcy = c.kpis_reporting ? cur : c.currency;
        return {
          lineNumber: i + 1,
          description: `${c.company_name}${c.is_parent ? ' (home)' : ''}`,
          cells: {
            ccy: c.currency,
            sales_mtd: moneyInCurrency(k.sales_mtd, displayCcy),
            gp: moneyInCurrency(k.gross_profit_ytd, displayCcy),
            np: moneyInCurrency(k.net_profit, displayCcy),
            cash: moneyInCurrency(k.cash_and_bank, displayCcy),
            ar: moneyInCurrency(k.receivables, displayCcy),
            inv: moneyInCurrency(k.inventory_value, displayCcy),
            neg_mgn: String(c.kpis.projects_negative_margin_count ?? '—'),
          },
        };
      }),
      finalNote: t
        ? `Group totals (${cur}): Sales MTD ${moneyInCurrency(t.sales_mtd, cur)} · GP ${moneyInCurrency(t.gross_profit_ytd, cur)} · Net ${moneyInCurrency(t.net_profit, cur)} · Cash ${moneyInCurrency(t.cash_and_bank, cur)} · AR ${moneyInCurrency(t.receivables, cur)} · Inventory ${moneyInCurrency(t.inventory_value, cur)} · Neg margin projects ${t.projects_negative_margin_count ?? '—'}${g.notes.length ? ` | Notes: ${g.notes.join(' ')}` : ''}`
        : g.notes.length
          ? `Notes: ${g.notes.join(' ')}`
          : undefined,
    });
  };

  const displayReporting = (v: number | null | undefined) =>
    !totals ? '—' : moneyInCurrency(v, reportingCurrency);

  const worryItems = useMemo(() => {
    return (data?.attention_queues ?? []).map((a) => ({
      area: a.area,
      metric: a.area,
      count: 0,
      severity: a.severity,
      message: `${a.company_name}: ${a.message}`,
      link: a.link,
    }));
  }, [data]);

  type WatchRow = { key: string; label: string; value: number | null | undefined };
  const watchRows: WatchRow[] = [
    { key: 'backlog', label: 'Order backlog', value: totals?.order_backlog },
    { key: 'pipeline', label: 'Pipeline', value: totals?.pipeline },
    { key: 'approvals', label: 'Approvals backlog', value: totals?.approvals_backlog },
    { key: 'dni', label: 'Delivered not invoiced', value: totals?.delivered_not_invoiced },
    { key: 'grn', label: 'GRN not invoiced (count)', value: totals?.grn_not_invoiced_count },
    { key: 'po', label: 'Open PO value', value: totals?.open_po_value },
    { key: 'ic', label: 'IC net (1160−2160)', value: totals?.ic_net_position },
  ];

  type MarginStripRow = GroupTopProject & {
    key: string;
    company_id: string;
    company_name: string;
    currency: string;
  };

  const marginStripRows: MarginStripRow[] = useMemo(() => {
    if (!data?.companies) return [];
    return data.companies.flatMap((c) =>
      (c.top_projects ?? []).map((p) => ({
        ...p,
        key: `${c.company_id}:${p.project_id}`,
        company_id: c.company_id,
        company_name: c.company_name,
        currency: c.currency,
      }))
    );
  }, [data]);

  const primaryKpis: Array<{
    label: string;
    value: string;
    accentTier?: ReturnType<typeof scoreToSignalTier>;
  }> = [
    { label: 'Sales today', value: displayReporting(totals?.sales_today) },
    { label: 'Sales MTD', value: displayReporting(totals?.sales_mtd) },
    { label: 'Gross profit YTD', value: displayReporting(totals?.gross_profit_ytd) },
    {
      label: 'Net profit',
      value: displayReporting(totals?.net_profit),
      accentTier:
        totals?.net_profit != null && totals.net_profit < 0 ? scoreToSignalTier(25) : undefined,
    },
    { label: 'Cash & bank', value: displayReporting(totals?.cash_and_bank) },
    { label: 'Receivables', value: displayReporting(totals?.receivables) },
    {
      label: 'Overdue AR',
      value: `${moneyInCurrency(totals?.overdue_ar_amount, reportingCurrency)}${
        totals?.overdue_ar_count != null ? ` (${totals.overdue_ar_count})` : ''
      }`,
      accentTier: (totals?.overdue_ar_count ?? 0) > 0 ? scoreToSignalTier(45) : undefined,
    },
    { label: 'Payables', value: displayReporting(totals?.payables) },
  ];

  const secondaryKpis = [
    { label: 'Inventory', value: displayReporting(totals?.inventory_value) },
    { label: 'Working capital', value: displayReporting(totals?.working_capital) },
    { label: 'Project site stock', value: displayReporting(totals?.project_material_balance) },
    {
      label: 'Budget overruns',
      value: String(totals?.project_overrun_count ?? '—'),
      statusDot:
        (totals?.project_overrun_count ?? 0) > 0 ? 'var(--oi-crimson)' : 'var(--oi-emerald)',
    },
    {
      label: 'Neg. margin projects',
      value: String(totals?.projects_negative_margin_count ?? '—'),
      statusDot:
        (totals?.projects_negative_margin_count ?? 0) > 0
          ? 'var(--oi-crimson)'
          : 'var(--oi-emerald)',
    },
    { label: 'HRMS headcount', value: String(totals?.hr_headcount ?? '—') },
    { label: 'Payroll cost MTD', value: displayReporting(totals?.hr_payroll_cost_mtd) },
    { label: 'OT hours MTD', value: String(totals?.hr_ot_hours_mtd ?? '—') },
    {
      label: 'OT % MTD',
      value:
        totals?.hr_ot_pct_mtd != null ? `${Number(totals.hr_ot_pct_mtd).toFixed(1)}%` : '—',
    },
    { label: 'Leave liability (days)', value: String(totals?.hr_leave_liability_days ?? '—') },
    { label: 'EOSB provision', value: displayReporting(totals?.hr_eosb_provision) },
    {
      label: 'HR docs expiring',
      value: String(totals?.hr_doc_expiry_open ?? '—'),
      statusDot:
        (totals?.hr_doc_expiry_open ?? 0) > 0 ? 'var(--oi-crimson)' : 'var(--oi-emerald)',
    },
  ];

  return (
    <OiPageFrame
      subtitle="Group view across companies you belong to — same platform tenant. Not IC consolidation."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Could not load group GM dashboard"
    >
      {data ? (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refresh
            </Button>
            <Button type="primary" icon={<PrinterOutlined />} onClick={() => handlePrint(data)}>
              Print group summary
            </Button>
            <Tag color={currencyMode === 'mixed' ? 'warning' : 'processing'}>
              {reportingCurrency || '—'} · {currencyModeLabel(currencyMode)}
            </Tag>
            <Text type="secondary" className="oi-text-muted">
              As of {new Date(data.as_of).toLocaleString()} · {data.group.ok_count}/
              {data.group.company_count} companies OK
              {(data.group.fx_converted_company_count ?? 0) > 0
                ? ` · ${data.group.fx_converted_company_count} FX-converted`
                : ''}
            </Text>
          </Space>

          {data.group.notes.length > 0 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Group notes"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {data.group.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              }
            />
          ) : null}

          {(totals?.overdue_ar_count ?? 0) > 0 ||
          (totals?.project_overrun_count ?? 0) > 0 ||
          (totals?.projects_negative_margin_count ?? 0) > 0 ? (
            <div className="oi-alert-strip" style={{ marginBottom: 16 }}>
              {(totals?.overdue_ar_count ?? 0) > 0 ? (
                <div className="oi-alert-strip__item">
                  Overdue AR across group: {totals?.overdue_ar_count} invoice(s)
                  {` · ${moneyInCurrency(totals?.overdue_ar_amount, reportingCurrency)}`}
                </div>
              ) : null}
              {(totals?.project_overrun_count ?? 0) > 0 ? (
                <div className="oi-alert-strip__item">
                  Open budget exceedances: {totals?.project_overrun_count}
                </div>
              ) : null}
              {(totals?.projects_negative_margin_count ?? 0) > 0 ? (
                <div className="oi-alert-strip__item">
                  Projects with negative margin: {totals?.projects_negative_margin_count}
                </div>
              ) : null}
            </div>
          ) : null}

          <Card
            title={
              <Space wrap>
                <span>Group roll-up</span>
                <Tag>
                  Reporting {reportingCurrency || '—'}
                  {showPartialTotals ? ' (partial)' : ''}
                </Tag>
              </Space>
            }
            className="oi-widget-grid"
            bordered={false}
            style={{ marginBottom: 16 }}
          >
            {!hasReportingTotals && !showPartialTotals ? (
              <Text type="secondary" className="oi-text-muted">
                No reporting-currency totals available yet.
              </Text>
            ) : (
              <>
                <Row gutter={[12, 12]}>
                  {primaryKpis.map((k) => (
                    <Col xs={12} sm={8} md={6} key={k.label}>
                      <PrimaryKpiCard label={k.label} value={k.value} accentTier={k.accentTier} />
                    </Col>
                  ))}
                </Row>
                <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                  {secondaryKpis.map((k) => (
                    <Col xs={12} sm={6} md={4} key={k.label}>
                      <SecondaryKpiCard label={k.label} value={k.value} statusDot={k.statusDot} />
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </Card>

          <Card title="Project margin risk" bordered={false} style={{ marginBottom: 16 }}>
            {marginStripRows.length === 0 ? (
              <Text type="secondary" className="oi-text-muted">
                No at-risk projects in the activity/budget candidate set.
              </Text>
            ) : (
              <Table
                size="small"
                rowKey="key"
                pagination={false}
                scroll={{ x: 900 }}
                dataSource={marginStripRows}
                columns={[
                  {
                    title: 'Company',
                    dataIndex: 'company_name',
                    render: (name: string) => <Text strong>{name}</Text>,
                  },
                  {
                    title: 'Project',
                    render: (_: unknown, r: MarginStripRow) => (
                      <Space direction="vertical" size={0}>
                        <Text>
                          {r.project_number} · {r.project_name}
                        </Text>
                        {r.any_bva_over ? <Tag color="error">BVA over</Tag> : null}
                      </Space>
                    ),
                  },
                  {
                    title: 'EAC',
                    align: 'right' as const,
                    render: (_: unknown, r: MarginStripRow) => (
                      <span className="oi-mono-value">{moneyInCurrency(r.eac, r.currency)}</span>
                    ),
                  },
                  {
                    title: 'Billed profit',
                    align: 'right' as const,
                    render: (_: unknown, r: MarginStripRow) => (
                      <span className="oi-mono-value">
                        {moneyInCurrency(r.billed_profit, r.currency)}
                      </span>
                    ),
                  },
                  {
                    title: 'Billed margin %',
                    align: 'right' as const,
                    render: (_: unknown, r: MarginStripRow) => (
                      <span className="oi-mono-value">
                        {r.billed_margin_pct == null ? '—' : `${r.billed_margin_pct}%`}
                      </span>
                    ),
                  },
                ]}
              />
            )}
          </Card>

          <Card title="Company profitability" bordered={false} style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="company_id"
              pagination={false}
              scroll={{ x: 1200 }}
              dataSource={data.companies}
              columns={[
                {
                  title: 'Company',
                  dataIndex: 'company_name',
                  fixed: 'left',
                  render: (name: string, row: GroupCompanyRow) => (
                    <Space direction="vertical" size={0}>
                      <Text strong>
                        {name}
                        {row.is_parent ? ' (home)' : ''}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }} className="oi-text-muted">
                        {row.company_code} · {row.currency}
                        {row.permissions_scope === 'active_jwt_fallback' ? ' · JWT perms' : ''}
                      </Text>
                      {row.status === 'error' ? <Tag color="error">Load error</Tag> : null}
                    </Space>
                  ),
                },
                {
                  title: 'Sales MTD',
                  align: 'right' as const,
                  render: (_: unknown, r: GroupCompanyRow) => (
                    <span className="oi-mono-value">
                      {moneyInCurrency(r.kpis.sales_mtd, r.currency)}
                    </span>
                  ),
                },
                {
                  title: 'GP YTD',
                  align: 'right' as const,
                  render: (_: unknown, r: GroupCompanyRow) => (
                    <span className="oi-mono-value">
                      {moneyInCurrency(r.kpis.gross_profit_ytd, r.currency)}
                    </span>
                  ),
                },
                {
                  title: 'Net profit',
                  align: 'right' as const,
                  render: (_: unknown, r: GroupCompanyRow) => (
                    <span className="oi-mono-value">
                      {moneyInCurrency(r.kpis.net_profit, r.currency)}
                    </span>
                  ),
                },
                {
                  title: 'Cash',
                  align: 'right' as const,
                  render: (_: unknown, r: GroupCompanyRow) => (
                    <span className="oi-mono-value">
                      {moneyInCurrency(r.kpis.cash_and_bank, r.currency)}
                    </span>
                  ),
                },
                {
                  title: 'AR / Overdue',
                  align: 'right' as const,
                  render: (_: unknown, r: GroupCompanyRow) => (
                    <span>
                      <span className="oi-mono-value">
                        {moneyInCurrency(r.kpis.receivables, r.currency)}
                      </span>
                      <br />
                      <Text type="danger" style={{ fontSize: 12 }} className="oi-mono-value">
                        {moneyInCurrency(r.kpis.overdue_ar_amount, r.currency)}
                      </Text>
                    </span>
                  ),
                },
                {
                  title: 'Inventory',
                  align: 'right' as const,
                  render: (_: unknown, r: GroupCompanyRow) => (
                    <span className="oi-mono-value">
                      {moneyInCurrency(r.kpis.inventory_value, r.currency)}
                    </span>
                  ),
                },
                {
                  title: 'Slow / Dead',
                  render: (_: unknown, r: GroupCompanyRow) =>
                    `${r.kpis.slow_moving_count ?? '—'} / ${r.kpis.dead_stock_count ?? '—'}`,
                },
                {
                  title: 'Projects',
                  render: (_: unknown, r: GroupCompanyRow) =>
                    `${r.kpis.project_count ?? '—'} · overruns ${r.kpis.project_overrun_count ?? '—'} · neg ${r.kpis.projects_negative_margin_count ?? '—'}`,
                },
                {
                  title: 'HRMS',
                  render: (_: unknown, r: GroupCompanyRow) =>
                    `HC ${r.kpis.hr_headcount ?? '—'} · Pay ${moneyInCurrency(r.kpis.hr_payroll_cost_mtd, r.currency)} · Exp ${r.kpis.hr_doc_expiry_open ?? '—'}`,
                },
                {
                  title: 'Actions',
                  fixed: 'right',
                  width: 220,
                  render: (_: unknown, row: GroupCompanyRow) => (
                    <Space wrap size={4}>
                      <Button
                        size="small"
                        type="link"
                        className="oi-ghost-action"
                        loading={switchingId === row.company_id}
                        icon={<SwapOutlined />}
                        onClick={() => drillToCompany(row, row.drill_links.owner_intelligence)}
                      >
                        OI
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        className="oi-ghost-action"
                        loading={switchingId === row.company_id}
                        onClick={() => drillToCompany(row, row.drill_links.cash)}
                      >
                        Cash
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        className="oi-ghost-action"
                        loading={switchingId === row.company_id}
                        onClick={() => drillToCompany(row, row.drill_links.projects)}
                      >
                        Projects
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={12}>
              <Card title="Attention queues" bordered={false}>
                <WorryAboutTodayPanel items={worryItems} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title="Also watching"
                bordered={false}
                extra={
                  <Text type="secondary" style={{ fontSize: 12 }} className="oi-text-muted">
                    {`Sums · ${reportingCurrency || '—'}${showPartialTotals ? ' (partial)' : ''}`}
                  </Text>
                }
              >
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  dataSource={watchRows}
                  columns={[
                    { title: 'KPI', dataIndex: 'label' },
                    {
                      title: 'Value',
                      align: 'right' as const,
                      render: (_: unknown, r: WatchRow) => {
                        if (!totals) return '—';
                        if (r.key === 'approvals' || r.key === 'grn') {
                          return (
                            <span className="oi-mono-value">
                              {r.value == null ? '—' : String(r.value)}
                            </span>
                          );
                        }
                        return (
                          <span className="oi-mono-value">
                            {moneyInCurrency(r.value ?? null, reportingCurrency)}
                          </span>
                        );
                      },
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </>
      ) : null}
    </OiPageFrame>
  );
};

export default GroupGmDashboardPage;
